/** @license
 *
 * Copyright 2018 Esri
 *
 * All rights reserved under the copyright laws of the United States and applicable international laws, treaties,
 * and conventions.
 *
 * This material is licensed for use under the Esri Master License Agreement (MLA), and is bound by the terms of
 * that agreement. You may redistribute and use this code without modification, provided you adhere to the terms
 * of the MLA and include this copyright notice.
 *
 * See use restrictions at http://www.esri.com/legal/pdfs/mla_e204_e300/english
 *
 * For additional information, contact: Environmental Systems Research Institute, Inc. Attn: Contracts and Legal
 * Services Department 380 New York Street Redlands, California, USA 92373 USA
 *
 * email: contracts@esri.com
 */

/**
 * GlobalNav manages the connection with the Esri global-nav library (https://github.com/Esri/global-nav). In particular,
 * it contains the user interface for logging in and out.
 *
 * @module GlobalNav
 */

//#region Interfaces
import * as common from "@esri/solution-common";
import * as esriGlobalNav from "esri-global-nav";
import * as gn from "./gn";
import * as helpers from "./helpers";
import * as intl from "@arcgis/core/intl";
import { Auth } from "./Auth";
//#endregion
// ------------------------------------------------------------------------------------------------------------------ //

interface IKeyedStrings {
  [key: string]: string;
}

export class GlobalNav {

  private _authentication: Auth | null = null;
  private _locale = "en";
  private _standaloneMode: boolean = true;

  // ---------------------------------------------------------------------------------------------------------------- //

  public constructor(
    locale = "en"
  ) {
    this._locale = intl.normalizeMessageBundleLocale(locale as any) ?? "en";
  }

  /**
   * Builds the global nav menus.
   *
   * @param standaloneMode Switch indicating if app is running in standalone mode
   * @param globalNavDefaultConfiguration Global nav configuration for the standalone version of the app
   * @param translation Translations for the global nav UI
   * @param authentication Credential manager with its sign in and sign out functions
   */
  public build(
    standaloneMode: boolean,
    globalNavDefaultConfiguration: any,
    translations: any,
    authentication: Auth
  ): Promise<void> {
    this.saveEventListeners();
    this._authentication = authentication;
    this._standaloneMode = standaloneMode;

    // Update the language text in the account section
    helpers.mergeTranslationsIntoGlobalNavAccountMenu(globalNavDefaultConfiguration, translations);

    // Build the header and footer
    let constructDef: Promise<any> = Promise.resolve(null);
    if (standaloneMode) {
      // Update the language text in the Solutions section
      helpers.mergeTranslationsIntoGlobalNavSolutionsMenu(globalNavDefaultConfiguration, translations);

      constructDef = this.buildStandaloneGlobalNav(globalNavDefaultConfiguration);
    } else {
      constructDef = this.buildGalleryGlobalNav(globalNavDefaultConfiguration, translations, authentication);
    }

     
    return constructDef.then(
      (globalNavConfig: any) => {
        // Initialize the Esri Global Navigation sections
        (document.getElementsByClassName("esri-header-client")[0] as HTMLElement).style.visibility = "hidden";

        // Connect to Global Nav sign-in
        const self = this;
        const globalNavHeader = document.getElementsByClassName(
          (globalNavConfig.headerElm || globalNavConfig.targetElm).substr(1) as string
        )[0];
        globalNavHeader.addEventListener("header:click:signin", () => authentication.signIn());
        globalNavHeader.addEventListener("header:click:signout", () => self.signOut());

        // Change resources button to open page in new tab rather than in place
        const resourcesButtons = document.querySelectorAll("[data-menu-id='resources-tab']");
        resourcesButtons.forEach(function (node) {
          node.setAttribute("target", "_blank");
        });

        // Set the current page in the gallery submenu
        if (window.location.hash === "#solutions") {
          this._setActiveTertiaryMenuTab(false);
        } else {
          this._setActiveTertiaryMenuTab(true);
        }

        // Update the account display with login info if we have it
        this.setAccountDisplay(globalNavConfig, authentication);

        // Patch some a11y problems
        this.performA11yPatching();

        (document.getElementsByClassName("esri-header-client")[0] as HTMLElement).style.visibility = "visible";
      }
    );
  }

  // ---------------------------------------------------------------------------------------------------------------- //

  /**
   * Sets up the event listeners for the buttons in the second- and third-level global nav menus.
   */
  private activateGallerySubmenus(): void {
    // Set up the listeners for the two buttons: the second and third level openers
    document.getElementById("second-nav-btn")?.addEventListener("click", () => {
      const container = document.getElementById("second-nav");
      const menu = document.getElementById("second-nav-menu");
      if (container && menu) {
        if (container.getAttribute("aria-expanded") === "false") {
          container.setAttribute("aria-expanded", "true");
          menu.setAttribute("aria-hidden", "false");
        } else {
          container.setAttribute("aria-expanded", "false");
          menu.setAttribute("aria-hidden", "true");
        }
      }
    });

    document.getElementById("third-nav-btn")?.addEventListener("click", () => {
      const container = document.getElementById("third-nav-btn");
      const menu = document.getElementById("third-nav-menu");
      if (container && menu) {
        if (container.getAttribute("aria-expanded") === "false") {
          container.setAttribute("aria-expanded", "true");
          menu.setAttribute("aria-hidden", "false");
          container.style.transform = "rotateX(180deg)";
          container.style.translate = "0px,1px";
        } else {
          container.setAttribute("aria-expanded", "false");
          menu.setAttribute("aria-hidden", "true");
          container.style.transform = "rotateX(0deg)";
          container.style.translate = "0px,0px";
        }
      }
    });
  }

  /**
   * Adds contents (string via innerHTML; array or single item via appendChild) HTML element.
   *
   * @param elem HTML element to modify
   * @param contents HTML elements to add to list
   */
  private addContents(elem: Element, contents?: any): void {
    if (typeof contents === "string") {
      elem.innerHTML = contents;
    } else if (Array.isArray(contents)) {
      contents.forEach(
        childElem => elem.appendChild(childElem)
      );
    } else if (contents) {
      elem.appendChild(contents);
    }
  }

  /**
   * Applies a set of attribute-value pairs to an HTML element.
   *
   * @param elem An HTML element to modify
   * @param attributes A set of attributes to set on the element, where the property is the attribute name and the
   * value is the attribute value
   */
  private applyHtmlAttributes(elem: Element, attributes: IKeyedStrings): void {
    Object.keys(attributes).forEach(
      key => elem.setAttribute(key, attributes[key])
    );
  }

  /**
   * Builds the gallery configuration for the global nav menus.
   *
   * @param globalNavDefaultConfiguration Global nav configuration for the standalone version of the app
   * @param translation Translations for the global nav UI
   * @param authentication Credential manager with its sign in and sign out functions
   * @return Gallery-style global nav configuration
   */
  private buildGalleryGlobalNav(
    globalNavDefaultConfiguration: any,
    translations: any,
    authentication: Auth
  ): Promise<esriGlobalNav.IGlobalNavConfig | esriGlobalNav.IGlobalNavPartialConfig> {
    return new Promise<esriGlobalNav.IGlobalNavConfig | esriGlobalNav.IGlobalNavPartialConfig>(resolve => {
      // Get the language-specific version of the Esri global nav parts
      const gnUrl = (document.location.origin + document.location.pathname)
        .replace("index.html", "")
        + `assets/nls-gn/${this._locale}.json`;
      void common.getJson(gnUrl, authentication.RestUserSession as common.UserSession)
      .then((globalNavEsriConfiguration: any) => {
         
        let globalNavEsriHeaderConfig = common.getProp(globalNavEsriConfiguration, "jcr:content.root.global_navigation_v2");
        if (!globalNavEsriHeaderConfig['menu-0']) {
          // Building Blocks tool used for esri.com causes the config to be placed in a sub prop
          for (const property in globalNavEsriHeaderConfig) {
            if (globalNavEsriHeaderConfig.hasOwnProperty(property)) {
              if (property.indexOf('global_navigation_v2') > -1) {
                globalNavEsriHeaderConfig = globalNavEsriHeaderConfig[property];
              }
            }
          }
        }
        globalNavEsriHeaderConfig = this.updateHeaderMenuTileUrls(globalNavEsriHeaderConfig, translations.$locale as string);

         
        let globalNavEsriFooterConfig = common.getProp(globalNavEsriConfiguration, "jcr:content.root.global_footer");
        if (!globalNavEsriFooterConfig.menu1links) {
          // Building Blocks tool used for esri.com causes the config to be placed in a sub prop
          for (const property in globalNavEsriFooterConfig) {
            if (globalNavEsriFooterConfig.hasOwnProperty(property)) {
              if (property.indexOf('global_footer') > -1) {
                globalNavEsriFooterConfig = globalNavEsriFooterConfig[property];
              }
            }
          }
        }
        globalNavEsriFooterConfig = this.updateFooterMenuTileUrls(globalNavEsriFooterConfig, translations.$locale as string);

        // Initialize a gallery
        const globalNavConfig = {
          headerElm: ".esri-header-barrier",
          footerElm: ".esri-footer-barrier",
          menuData: {
            lang: translations.$locale,
            header: {
              brand: {
                label: "",
                image: "./assets/images/global-navigation/esri-logo-globe-tm.svg",
                href: "https://www.esri.com/en-us/home",
                brandText: ""
              },
              account: globalNavDefaultConfiguration.header.account,
              menus: gn.buildHeaderMenus(globalNavEsriHeaderConfig)
            },
            footer: {
              brand: {
                label: "",
                href: "https://www.esri.com/en-us/about/about-esri/overview",
                path: "./assets/images/Science_of_Where_icon.svg"
              },
              social: { // esri-footer-section--1
                label: "",
                menu: gn.buildFooterSocialMenu()
              },
              menu: { // esri-footer-section--2
                label: "",
                menu: gn.buildFooterMenu(globalNavEsriFooterConfig)
              },
              info: { // esri-footer-section--3
                label: "",
                menu: gn.buildFooterInfoMenu(globalNavEsriFooterConfig)
              }
            }
          }
        } as esriGlobalNav.IGlobalNavConfig;
        esriGlobalNav.create(globalNavConfig);

        const galleryBannerContainer: Element = document.getElementsByClassName("esri-header-barrier")[0];
        galleryBannerContainer.appendChild(this.createGallerySubmenus(translations));
        galleryBannerContainer.appendChild(this.createGalleryBanner(translations));
        this.activateGallerySubmenus();
        this.createEsriSearch(translations);
        this.createSwitchLanguageModal(translations);
        this.updateAriaHidden();
        window.onresize = this.updateAriaHidden;


        resolve(globalNavConfig);
      });
    });
  }

  /**
   * Builds the standalone configuration for the global nav menus.
   *
   * @param globalNavDefaultConfiguration Global nav configuration for the standalone version of the app
   * @return Standalone-style global nav onfiguration
   */
  private buildStandaloneGlobalNav(
    globalNavDefaultConfiguration: any
  ): Promise<esriGlobalNav.IGlobalNavConfig | esriGlobalNav.IGlobalNavPartialConfig> {
    return new Promise<esriGlobalNav.IGlobalNavConfig | esriGlobalNav.IGlobalNavPartialConfig>(resolve => {
      // Initialize the standard Esri header
      const globalNavConfig = {
        targetElm: ".esri-header-barrier",
        menuData: globalNavDefaultConfiguration
      } as esriGlobalNav.IGlobalNavPartialConfig;
      esriGlobalNav.createHeader(globalNavConfig);

      resolve(globalNavConfig);
    });
  }

  /**
   * Clears the text in the Esri-site search modal.
   */
  private clearSearchEsriModal() {
    const searchTextInput = (document.getElementById("search-esri-input") as HTMLInputElement);
    searchTextInput.value = "";
  }

  /**
   * Closes the Switch Language modal and, if the locale has been changed, reloads the page with the new locale.
   */
  private closeSwitchLanguageModal() {
    const localeEsriModal = document.getElementById("switch-locale-modal") as HTMLCalciteModalElement;
    if (localeEsriModal) {
      localeEsriModal.open = false;
    }

    // Switch locale if the selection changed
    let oldLocale = document.querySelector("html")?.getAttribute("lang");
    const list = document.getElementById("switch-locale-list");
    let newLocale = (list as any).options[(list as any).selectedIndex].value;
    if (oldLocale !== newLocale) {
      let pageUrl = window.location.href;
      oldLocale = `locale=${oldLocale}`;
      newLocale = `locale=${newLocale}`;
      if (pageUrl.indexOf(oldLocale) > 0) {
        pageUrl = pageUrl.replace(oldLocale, newLocale as string);
      } else {
        const iSearchStart = pageUrl.indexOf("?") + 1;
        pageUrl = pageUrl.substr(0, iSearchStart) + newLocale + "&" + pageUrl.substr(iSearchStart);
      }
      window.location.assign(pageUrl);
    }
  }

  /**
   * Creates a button HTML element.
   *
   * @param attributes A set of attributes to set on the element, where the property is the attribute name and the
   * value is the attribute value
   * @param label Text to display on button
   * @return HTML element
   */
  private createButton(attributes: IKeyedStrings, label?: string): Element {
    const elem = document.createElement("button");
    this.applyHtmlAttributes(elem, attributes);
    if (label) {
      elem.innerHTML = label;
    }

    return elem;
  }

  /**
   * Creates a calcite-action HTML element.
   *
   * @param attributes A set of attributes to set on the element, where the property is the attribute name and the
   * value is the attribute value
   * @return HTML element
   */
  private createCalciteAction(attributes: IKeyedStrings): Element {
    if (!attributes.text) {
      attributes.text = "";  // required property
    }
    const elem = document.createElement("calcite-action");
    this.applyHtmlAttributes(elem, attributes);

    return elem;
  }

  /**
   * Creates a calcite-button HTML element.
   *
   * @param attributes A set of attributes to set on the element, where the property is the attribute name and the
   * value is the attribute value
   * @param label Text to display on button
   * @return HTML element
   */
  private createCalciteButton(attributes: IKeyedStrings, label?: string): Element {
    const elem = document.createElement("calcite-button");
    this.applyHtmlAttributes(elem, attributes);
    if (label) {
      elem.innerHTML = label;
    }

    return elem;
  }

  /**
   * Creates a container HTML element.
   *
   * @param containerType Type of HTML element to create, e.g., "div" or "calcite-modal"
   * @param attributes A set of attributes to set on the element, where the property is the attribute name and the
   * value is the attribute value
   * @param contents HTML elements to add to container: a string via innerHTML; an array or single item via appendChild
   * @return HTML element
   */
  private createContainer(
    containerType: "calcite-modal" | "div" | "li" | "list" | "nav" | "select" | "span",
    attributes: IKeyedStrings,
    contents?: any
  ): Element {
    const elem = document.createElement(containerType);
    this.applyHtmlAttributes(elem, attributes);
    this.addContents(elem, contents);

    return elem;
  }

  /**
   * Creates the Esri search feature.
   *
   * @param translation Translations for the global nav UI
   */
  private createEsriSearch(
    translations: any
  ): void {
    const self = this;

    // Create the button to launch the search
    const triggerBtn = this.createButton({
      "id": "search-esri-btn",
      "aria-label": translations.header.esriSearchTitle
    });

    // Find the header line break (there are hidden ones to avoid)
    const header = document.getElementsByClassName("esri-header")[0];
    const lineBreaks = document.getElementsByClassName("esri-header-lineBreak");
    // lineBreaks: "Type 'HTMLCollectionOf<Element>' is not an array type or a string type."
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < lineBreaks.length; i++) {
      if (lineBreaks[i].parentNode === header) {
        header.insertBefore(triggerBtn, lineBreaks[i]);
        break;
      }
    }

    // Set the click action
    (triggerBtn as HTMLCalciteButtonElement).onclick = function () {
      self.toggleSearchEsriModal();
    };

    // Create the search dialog
    const searchEsriCancelBtn = this.createCalciteButton(
      {
        "slot": "secondary",
        "id": "search-esri-cancel-btn",
        "appearance": "outline"
      },
      translations.header.esriSearchCancelBtn as string
    );
    (searchEsriCancelBtn as HTMLCalciteButtonElement).onclick = function () {
      self.toggleSearchEsriModal();
    };

    const searchEsriProceedBtn = this.createCalciteButton(
      {
        "slot": "primary",
        "id": "search-esri-proceed-btn"
      },
      translations.header.esriSearch as string
    );
    (searchEsriProceedBtn as HTMLCalciteButtonElement).onclick = function () {
      self.performEsriSearch();
    };

    const searchInput = this.createInput(
      {
        "slot": "content",
        "id": "search-esri-input",
        "type": "search"
      }
    );
    (searchInput as HTMLCalciteButtonElement).onkeypress = function (keyEvent) {
      if (keyEvent.key === "Enter") {
        self.performEsriSearch();
      }
    };

    const searchEsriModal = this.createContainer("calcite-modal",
      {
        "id": "search-esri-modal",
        "aria-labelledby": "search-esri-title",
        "intl-close": translations.header.esriSearchCancelBtn
      },
      [
        this.createContainer("div",
          {
            "slot": "header",
            "id": "switch-locale-title"
          },
          translations.header.esriSearchTitle
        ),
        searchInput,
        searchEsriCancelBtn,
        searchEsriProceedBtn
      ]
    );
    (searchEsriModal as HTMLCalciteModalElement).beforeClose =
      () => {
        return new Promise<void>((resolve) => {
          self.clearSearchEsriModal();
          resolve();
        });
      };

    document.querySelector("body")?.appendChild(searchEsriModal);
  }

  /**
   * Creates the second- and third-level global nav menus.
   *
   * @param translation Translations for the global nav UI
   * @return HTML element containing menus
   */
  private createGallerySubmenus(
    translations: any
  ): Element {
    /*
    <div class="local-navigation aem-GridColumn aem-GridColumn--default--12">
      <nav class="second-nav" id="second-nav" aria-label="Main" role="navigation" aria-expanded="false" style="box-shadow: none;">
        <div class="nav-title" id="nav-title">
          <a class="nav-titlelink" href="https://www.esri.com/en-us/arcgis/products/arcgis-solutions/overview">ArcGIS Solutions</a>
        </div>
        <div class="nav-menu">
          <button class="nav-menu-title" href="#"></button>
          <div class="nav-submenu">
            <ul class="nav-sublist" aria-labelledby="nav-title">
              <li class="nav-subitem"><a class="es-nav-sublink-local" id="Overview" href="https://www.esri.com/en-us/arcgis/products/arcgis-solutions/overview">Overview</a></li>
              <li class="nav-subitem"><a class="es-nav-sublink-local" id="Resources" href="https://www.esri.com/en-us/arcgis/products/arcgis-pro/resources">Resources</a></li>
              <li class="nav-subitem"><a class="es-nav-sublink-local" id="Gallery" href="https://www.esri.com/en-us/arcgis/products/arcgis-pro/extensions">Gallery</a></li>
            </ul>
          </div>
        </div>
      </nav>
      <div class="third-nav-bar">
        <button class="third-nav-title" aria-expanded="false" href="#"></button>
        <div class="grid-container">
          <nav class="third-nav-bar-container" aria-hidden="false" aria-label="Tertiary Navigation">
            <ul class="third-nav-menu">
              <li class="ternav-menu-item" aria-current="false"><a class="ternav-menu-item-link" href="https://www.esri.com/en-us/arcgis/products/arcgis-solutions/overview" aria-current="true">Home</a></li>
              <li class="ternav-menu-item" aria-current="false"><a class="ternav-menu-item-link" href="http://tschudim.esri.com/ag_/solutions_gallery/doc.html" aria-current="true">My Solutions</a></li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
    */
    return this.createContainer("div",
      {
        "class": "local-navigation aem-GridColumn aem-GridColumn--default--12"
      }, [
      this.createContainer("nav",
        {
          "class": "second-nav",
          "id": "second-nav",
          "aria-label": "Main",
          "role": "navigation",
          "aria-expanded": "false",
          "style": "box-shadow: none;"
        },
        [
          this.createContainer("div",
            {
              "class": "nav-title"
            },
            [
              this.createLink(
                {
                  "class": "nav-titlelink",
                  "href": `https://www.esri.com/${this.generateSolutionsOverviewUrlLocaleCode(translations.$locale as string)}/arcgis/products/arcgis-solutions/overview`
                },
                "ArcGIS Solutions")
            ]),
          this.createContainer("div",
            {
              "class": "nav-menu"
            },
            [
              this.createButton(
                {
                  "class": "nav-menu-title",
                  "id": "second-nav-btn",
                  "aria-label": translations.header.menus[2][0].label + " " + translations.header.menus[1][0].label,
                  "href": "#"
                }),
              this.createContainer("div",
                {
                  "class": "nav-submenu",
                  "id": "second-nav-menu",
                  "aria-hidden": "true"
                },
                [
                  this.createContainer("list",
                    {
                      "class": "nav-sublist"
                    }, [
                    this.createContainer("li",
                      {
                        "class": "nav-subitem"
                      },
                      [
                        this.createLink(
                          {
                            "class": "es-nav-sublink-local",
                            "id": "Overview",
                            "aria-label": translations.header.menus[2][0].label,
                            "href": `https://www.esri.com/${this.generateSolutionsOverviewUrlLocaleCode(translations.$locale as string)}/arcgis/products/arcgis-solutions/overview`
                          },
                          translations.header.menus[2][0].label as string)
                      ]
                    ),
                    this.createContainer("li",
                      {
                        "class": "nav-subitem"
                      },
                      [
                        this.createLink(
                          {
                            "class": "es-nav-sublink-local",
                            "id": "Resources",
                            "aria-label": translations.header.menus[1][0].label,
                            "href": "https://www.esri.com/en-us/arcgis/products/arcgis-solutions/resources"
                          },
                          translations.header.menus[1][0].label as string)
                      ]
                    )
                  ])
                ])
            ]),
          this.createLink(
            {
              "class": "call-to-action",
              "id": "Gallery",
              "aria-label": translations.header.menus[2][1].label,
              "href": "#"
            },
            translations.header.menus[2][1].label as string)
        ]),
      this.createContainer("div",
        {
          "class": "third-nav-bar"
        },
        [
          this.createButton(
            {
              "class": "third-nav-title",
              "id": "third-nav-btn",
              "aria-expanded": "false",
              "aria-label": translations.header.menus[0][0].label + " " + translations.header.menus[0][1].label,
              "href": "#"
            }),
          this.createContainer("div",
            {
              "class": "nav-grid-container"
            },
            [
              this.createContainer("nav",
                {
                  "class": "third-nav-bar-container",
                  "id": "third-nav-menu",
                  "aria-hidden": "true"
                }, [
                this.createContainer("list",
                  {
                    "class": "third-nav-menu"
                  },
                  [
                    this.createContainer("li",
                      {
                        "class": "ternav-menu-item"
                      },
                      [
                        this.createContainer("span",
                          {
                            "class": "ternav-menu-item-link",
                            "id": "home-submenu-link",
                            "aria-current": "false",
                            "aria-label": translations.header.menus[0][0].label,
                            "data-menu-id": "tab-1",
                            "data-menu-type": "nav-menu",
                            "tabindex": "0"
                          },
                          translations.header.menus[0][0].label)
                      ]
                    ),
                    this.createContainer("li",
                      {
                        "class": "ternav-menu-item"
                      },
                      [
                        this.createContainer("span",
                          {
                            "class": "ternav-menu-item-link",
                            "id": "solutions-submenu-link",
                            "aria-current": "false",
                            "aria-label": translations.header.menus[0][1].label,
                            "data-menu-id": "tab-2",
                            "data-menu-type": "nav-menu",
                            "tabindex": "0"
                          },
                          translations.header.menus[0][1].label)
                      ]
                    )
                  ])
              ])
            ])
        ])
    ]);
  }

  /**
   * Creates the "hero banner".
   *
   * @param translation Translations for the global nav UI
   * @return HTML element containing banner
   */
  private createGalleryBanner(
    translations: any
  ): Element {
    /*
    <div class="gallery-banner-background">
      <div class="gallery-banner">
        <div class="gallery-banner-title-div">
          <div class="gallery-banner-title">ArcGIS Solutions</div>
          <div class="gallery-banner-separator"></div>
          <div class="gallery-banner-subtitle">Industry-specific configurations for ArcGIS</div>
        </div>
        <img src="assets/images/arcgis-solutions-overview-banner-493x268.png" class="gallery-banner-icon"/>
      </div>
    </div>
    */
    return this.createContainer("div",
      {
        "class": "gallery-banner-background"
      },
      [
        this.createContainer("div",
          {
            "class": "gallery-banner"
          },
          [
            this.createContainer("div",
              {
                "class": "gallery-banner-title-div"
              },
              [
                this.createContainer("div",
                  {
                    "class": "gallery-banner-title"
                  },
                  "ArcGIS<span class='line-break'> </span>Solutions"),
                this.createContainer("div",
                  {
                    "class": "gallery-banner-separator"
                  },
                  [
                  ]),
                this.createContainer("div",
                  {
                    "class": "gallery-banner-subtitle"
                  },
                  translations.header.subtitle)
              ]),
            this.createImg(
              {
                "class": "gallery-banner-icon",
                "src": "assets/images/arcgis-solutions-overview-banner-493x268.png"
              })
          ])
      ]);
  }

  /**
   * Creates an image HTML element.
   *
   * @param attributes A set of attributes to set on the element, where the property is the attribute name and the
   * value is the attribute value
   * @return HTML element
   */
  private createImg(attributes: IKeyedStrings): Element {
    const elem = document.createElement("img");
    this.applyHtmlAttributes(elem, attributes);

    return elem;
  }

  /**
   * Creates an input HTML element.
   *
   * @param attributes A set of attributes to set on the element, where the property is the attribute name and the
   * value is the attribute value
   * @return HTML element
   */
  private createInput(attributes: IKeyedStrings): Element {
    const elem = document.createElement("input");
    this.applyHtmlAttributes(elem, attributes);

    return elem;
  }

  /**
   * Creates a link HTML element.
   *
   * @param attributes A set of attributes to set on the element, where the property is the attribute name and the
   * value is the attribute value
   * @param label Text to display on link
   * @return HTML element
   */
  private createLink(attributes: IKeyedStrings, label: string): Element {
    const elem = document.createElement("a");
    this.applyHtmlAttributes(elem, attributes);
    elem.innerHTML = label;

    return elem;
  }

  /**
   * Creates an option HTML element for use within a select HTML element.
   *
   * @param value Value to associate with option for later retrieval as part of the select HTML element
   * @param label Text to display on option
   * @return HTML element
   */
  private createSelectOption(value: string, label: string): Element {
    const elem = document.createElement("option");
    this.applyHtmlAttributes(elem, { "value": value });
    elem.innerHTML = label;

    return elem;
  }

  /**
   * Creates the change-language dialog.
   *
   * @param translation Translations for the global nav UI
   */
  private createSwitchLanguageModal(
    translations: any
  ): void {
    /*
    <div class="esri-footer-language">
      <button class="esri-footer-language-control" title="Current language, English (Global)">Switch Language</button>
    </div>

    <calcite-modal id="switch-locale-modal" aria-labelledby="switch-locale-title">
      <div slot="header" id="switch-locale-title0">Please select a language (<span id="switch-locale-title"></span>):</div>
      <div slot="content" id="switch-locale-content">
        <select id="switch-locale-list" class="esri-footer-language-dialog-choice">
          <option value="ar">عربي‎&nbsp;(Arabic)</option>
          <option value="bs">Bosanski&nbsp;(Bosnian)</option>
          <option value="ca">Catalan&nbsp;(Catalan)</option>
          <option value="cs">čeština&nbsp;(Czech‎)</option>
          <option value="da">Dansk&nbsp;(Danish)</option>
          <option value="de">Deutsch&nbsp;(German)</option>
          <option value="el">Ελλάδα&nbsp;(Greek‎)</option>
          <option value="en">English&nbsp;(English)</option>
          <option value="es">Español&nbsp;(Spanish)</option>
          <option value="et">Eesti&nbsp;(Estonian)</option>
          <option value="fi">Suomi&nbsp;(Finnish)</option>
          <option value="fr">Français&nbsp;(French)</option>
          <option value="he">עברית&nbsp;&nbsp;(Hebrew‎)</option>
          <option value="hr">Hrvatski&nbsp;(Croatian)</option>
          <option value="hu">Hungarian&nbsp;(Hungarian)</option>
          <option value="id">Bahasa Indonesia&nbsp;(Indonesian)</option>
          <option value="it">Italiano&nbsp;(Italian)</option>
          <option value="ja">日本語&nbsp;(Japanese)</option>
          <option value="ko">한국어&nbsp;(Korean)</option>
          <option value="lt">Lietuvių&nbsp;(Lithuanian‎)</option>
          <option value="lv">Latviešu&nbsp;(Latvian)</option>
          <option value="nb">Norsk&nbsp;(Norwegian)</option>
          <option value="nl">Nederlands&nbsp;(Dutch)</option>
          <option value="pl">Polski&nbsp;(Polish)</option>
          <option value="pt-br">Portuguese&nbsp;(Brazil)</option>
          <option value="pt-pt">Portuguese&nbsp;(Portugal)</option>
          <option value="ro">Română&nbsp;(Romanian)</option>
          <option value="ru">Русский&nbsp;(Russian)</option>
          <option value="sk">Slovenský&nbsp;(Slovak)</option>
          <option value="sl">Slovenian&nbsp;(Slovenian)</option>
          <option value="sr">Srpski&nbsp;(Serbian)</option>
          <option value="sv">Svenska&nbsp;(Swedish)</option>
          <option value="th">ไทย‎&nbsp;(Thai)</option>
          <option value="tr">Türkçe&nbsp;(Turkish)</option>
          <option value="uk">Українська&nbsp;(Ukrainian)</option>
          <option value="vi">Tiếng Việt&nbsp;(Vietnamese‎)</option>
          <option value="zh-cn">简体中文&nbsp;(Simplified Chinese)</option>
          <option value="zh-hk">繁體中文‏（香港‏）&nbsp;(Traditional Chinese (Hong Kong))</option>
          <option value="zh-tw">繁體中文‏（台灣‏）&nbsp;(Traditional Chinese (Taiwan))</option>
        </select>
      </div>
      <calcite-button id="switch-locale-button0" slot="primary" onclick="closeSwitchLanguageModal()">Change (<span id="switch-locale-button"></span>)</calcite-button>
    </calcite-modal>
    */
    const self = this;

    // Create the button to open the Switch Language dialog
    const triggerBtn = this.createButton(
      {
        "class": "esri-footer-language-control",
        "id": "switch-language-btn"
      }, translations.footer.switchLanguageBtn as string
    );
    document.getElementsByClassName("esri-footer-language")[0].appendChild(triggerBtn);

    // Set the click action
    (triggerBtn as HTMLCalciteButtonElement).onclick = function () {
      self.openSwitchLanguageModal();
    };

    // Create the change dialog
    const switchDialogChangeBtn = this.createCalciteButton(
      {
        "slot": "primary",
        "id": "switch-locale-button"
      },
      translations.footer.switchDialogChangeBtn as string
    );
    (switchDialogChangeBtn as HTMLCalciteButtonElement).onclick = function () {
      self.closeSwitchLanguageModal();
    };

    const switchLanguageModal = this.createContainer("calcite-modal",
      {
        "id": "switch-locale-modal",
        "aria-labelledby": "switch-locale-title",
        "intl-close": translations.footer.switchDialogCloseBtn
      },
      [
        this.createContainer("div",
          {
            "slot": "header",
            "id": "switch-locale-title"
          },
          translations.footer.switchDialogTitle
        ),
        this.createContainer("div",
          {
            "slot": "content",
            "id": "switch-locale-content"
          },
          this.createContainer("select",
            {
              "id": "switch-locale-list",
              "class": "esri-footer-language-dialog-choice"
            },
            [
              this.createSelectOption("ar", "عربي&nbsp;(Arabic)"),
              this.createSelectOption("bs", "Bosanski&nbsp;(Bosnian)"),
              this.createSelectOption("ca", "Catalan&nbsp;(Catalan)"),
              this.createSelectOption("cs", "čeština&nbsp;(Czech)"),
              this.createSelectOption("da", "Dansk&nbsp;(Danish)"),
              this.createSelectOption("de", "Deutsch&nbsp;(German)"),
              this.createSelectOption("el", "Ελλάδα&nbsp;(Greek)"),
              this.createSelectOption("en", "English&nbsp;(English)"),
              this.createSelectOption("es", "Español&nbsp;(Spanish)"),
              this.createSelectOption("et", "Eesti&nbsp;(Estonian)"),
              this.createSelectOption("fi", "Suomi&nbsp;(Finnish)"),
              this.createSelectOption("fr", "Français&nbsp;(French)"),
              this.createSelectOption("he", "עברית&nbsp;&nbsp;(Hebrew)"),
              this.createSelectOption("hr", "Hrvatski&nbsp;(Croatian)"),
              this.createSelectOption("hu", "Hungarian&nbsp;(Hungarian)"),
              this.createSelectOption("id", "Bahasa Indonesia&nbsp;(Indonesian)"),
              this.createSelectOption("it", "Italiano&nbsp;(Italian)"),
              this.createSelectOption("ja", "日本語&nbsp;(Japanese)"),
              this.createSelectOption("ko", "한국어&nbsp;(Korean)"),
              this.createSelectOption("lt", "Lietuvių&nbsp;(Lithuanian)"),
              this.createSelectOption("lv", "Latviešu&nbsp;(Latvian)"),
              this.createSelectOption("nb", "Norsk&nbsp;(Norwegian)"),
              this.createSelectOption("nl", "Nederlands&nbsp;(Dutch)"),
              this.createSelectOption("pl", "Polski&nbsp;(Polish)"),
              this.createSelectOption("pt-br", "Portuguese&nbsp;(Brazil)"),
              this.createSelectOption("pt-pt", "Portuguese&nbsp;(Portugal)"),
              this.createSelectOption("ro", "Română&nbsp;(Romanian)"),
              this.createSelectOption("ru", "Русский&nbsp;(Russian)"),
              this.createSelectOption("sk", "Slovenský&nbsp;(Slovak)"),
              this.createSelectOption("sl", "Slovenian&nbsp;(Slovenian)"),
              this.createSelectOption("sr", "Srpski&nbsp;(Serbian)"),
              this.createSelectOption("sv", "Svenska&nbsp;(Swedish)"),
              this.createSelectOption("th", "ไทย&nbsp;(Thai)"),
              this.createSelectOption("tr", "Türkçe&nbsp;(Turkish)"),
              this.createSelectOption("uk", "Українська&nbsp;(Ukrainian)"),
              this.createSelectOption("vi", "Tiếng Việt&nbsp;(Vietnamese)"),
              this.createSelectOption("zh-cn", "简体中文&nbsp;(Simplified Chinese)"),
              this.createSelectOption("zh-hk", "繁體中文（香港）&nbsp;(Traditional Chinese (Hong Kong))"),
              this.createSelectOption("zh-tw", "繁體中文（台灣）&nbsp;(Traditional Chinese (Taiwan))")
            ]
          )
        ),
        switchDialogChangeBtn
      ]
    );

    document.querySelector("body")?.appendChild(switchLanguageModal);
  }

  /**
   * Dispatches a CustomEvent.
   *
   * @param dispatcher HTML element dispatching event
   * @param type Name of the event
   * @param detail Event-dependent value associated with event
   */
  private dispatchPrivateEvent(
    dispatcher: Element,
    type: string,
    detail?: any
  ): void {
    const event = new CustomEvent(type, { detail });
    dispatcher.dispatchEvent(event);
  }

  /**
   * Converts a locale into the expanded locale used in the Solutions overview URL.
   *
   * @param locale Locale code used in the deployment app
   * @return Solutions overview locale code
   */
  private generateSolutionsOverviewUrlLocaleCode(
    locale: string
  ): string {
    const overviewUrlMapping = {
      "ar": "ar-sa",
      "bs": "bs-ba",
      "ca": "ca-es",
      "cs": "cs-cz",
      "da": "da-dk",
      "de": "de-de",
      "el": "el-gr",
      "en": "en-us",
      "es": "es-es",
      "et": "et-ee",
      "fi": "fi-fi",
      "fr": "fr-fr",
      "he": "he-il",
      "hr": "hr-hr",
      "hu": "hu-hu",
      "id": "id-id",
      "it": "it-it",
      "ja": "ja-jp",
      "ko": "ko-kr",
      "lt": "lt-lt",
      "lv": "lv-lv",
      "nb": "nb-no",
      "nl": "nl-nl",
      "no": "nb-no",
      "pl": "pl-pl",
      "pt-br": "pt-br",
      "pt-pt": "pt-pt",
      "ro": "ro-ro",
      "ru": "ru-ru",
      "sk": "en-us", // not yet supported
      "sl": "sl-sl",
      "sr": "sr-rs",
      "sv": "sv-se",
      "th": "th-th",
      "tr": "tr-tr",
      "uk": "uk-ua",
      "vi": "vi-vn",
      "zh-cn": "zh-cn",
      "zh-hk": "zh-hk",
      "zh-tw": "zh-tw"
    } as IKeyedStrings;
    return overviewUrlMapping[locale] || "en-us";
  }

  /**
   * Opens the the Switch Language modal.
   */
  private openSwitchLanguageModal() {
    this.setSwitchLanguageModalLocale();
    const localeEsriModal = document.getElementById("switch-locale-modal") as HTMLCalciteModalElement;
    if (localeEsriModal) {
      localeEsriModal.open = true;
    }
  }

  /**
   * Launches a search on esri.com using the value in the modal input field "search-esri-input".
   */
  private performEsriSearch() {
    const searchTextInput = (document.getElementById("search-esri-input") as HTMLInputElement);
    if (searchTextInput.value) {
      const searchURL = `https://www.esri.com/en-us/search?q=${encodeURIComponent(searchTextInput.value)}`;
      (window as any).open(searchURL, "_blank").focus();
    }

    // Close the search dialog
    this.toggleSearchEsriModal(true);
  }

  /**
   * Saves event listeners prior to inserting an interceptor to adding event listener.
   */
  private saveEventListeners(
  ): void {
    // Save actual addEventListener
    // eslint-disable-next-line @typescript-eslint/unbound-method
    (Element.prototype as any)._addEventListener = Element.prototype.addEventListener;

    // Define interceptor
    Element.prototype.addEventListener = function (eventType: string, listener: any, options: any) {
      // Pass-thru call to actual addEventListener
      (this as any)._addEventListener(eventType, listener, options);

      // Add event and its function to list in element
      if (!(this as any).eventListenerList) (this as any).eventListenerList = {};
      if (!(this as any).eventListenerList[eventType]) (this as any).eventListenerList[eventType] = [];
      (this as any).eventListenerList[eventType].push(listener);
    };
  }

  /**
   * Sets the user information display for the currently-logged-in user.
   *
   * @param configuration Global nav configuration information
   * @param authentication Credential manager with its portal information
   */
  private setAccountDisplay(
    configuration: any,
    authentication: Auth
  ): void {
    // Update global nav account section
    const portalSubset = authentication.PortalSubset;
    const portalUserSubset = authentication.PortalUserSubset;

    if (portalSubset && portalUserSubset) {
      this.updateAccountSection(configuration, {
        "user": {
          "name": portalUserSubset.fullName,
          "id": portalUserSubset.username,
          "group": portalSubset.name,
          "image": portalUserSubset.thumbnailUrl
        },
        "profileAndSettingsUrl": authentication.PortalUrl + "/home/user.html"
      });
    }
  }

  /**
   * Sets the Switch Language modal's current locale based on the page's locale.
   */
  private setSwitchLanguageModalLocale() {
    // Set the locale in the pick list
    const locale = document.querySelector("html")?.getAttribute("lang") as string;
    const supportedLocales = "ar|bs|ca|cs|da|de|el|en|es|et|fi|fr|he|hr|hu|id|it|ja|ko|lt|lv|nb|nl|no|pl|pt-br|pt-pt|ro|ru|sk|sl|sr|sv|th|tr|uk|vi|zh-cn|zh-hk|zh-tw";
    const iLocale = supportedLocales.split("|")?.indexOf(locale);
    (document.getElementById("switch-locale-list") as any).selectedIndex = iLocale;
  }

  /**
   * Toggles the Esri-site search modal.
   *
   * @param forceClose If true, forces the modal to close rather than toggling its state
   */
  private toggleSearchEsriModal(
    forceClose = false
  ) {
    const searchEsriModal = document.getElementById("search-esri-modal") as HTMLCalciteModalElement;
    if (searchEsriModal) {
      if (searchEsriModal.open || forceClose) {
        // Close search modal
        searchEsriModal.open = false;
        this.clearSearchEsriModal();
      } else {
        // Open search modal
        searchEsriModal.open = true;
      }
    }
  }

  /**
   * Updates the user information display for the currently-logged-in user.
   *
   * @param configuration Global nav configuration information
   * @param accountUpdate Account information
   */
  private updateAccountSection(
    configuration: any,
    accountUpdate: any
  ): void {
    if (configuration.menuData.header && configuration.menuData.header.account) {
      configuration.menuData.header.account.user = accountUpdate.user;
      if (configuration.menuData.header.account.menus) {
        configuration.menuData.header.account.menus[0].href = accountUpdate.profileAndSettingsUrl;
      }

      // Inform global nav header
      const globalNavHeaderAcct = document.getElementsByClassName("esri-header-account")[0];
      this.dispatchPrivateEvent(globalNavHeaderAcct, "header:update:account",
        configuration.menuData.header.account);

      const globalNavHeader = document.getElementsByClassName("esri-header")[0];
      this.dispatchPrivateEvent(globalNavHeader, "header:menu:close");

      // Remove "Switch Account" control button
      this.removeElement(document.getElementsByClassName("esri-header-account-signin-control -switch")[0]);
    }
  }

  /**
   * Converts the relative URLs in the footer to absolute URLs, using language-specific versions if possible.
   *
   * @param globalNavEsriFooterConfig Global nav configuration information
   * @param locale The two-letter locale code
   * @return A modified copy of configuration
   */
  private updateFooterMenuTileUrls(
    globalNavEsriFooterConfig: any,
    locale: string
  ): any {
    // trust.arcgis.com is only available for some languages
    if ("bs|ca|el|et|he|hr|hu|id|lt|lv|nb|nl|no|sk|sl|sr|sv|th|uk|vi|zh-hk|zh-tw".split("|").includes(locale)) {
      locale = "en";
    }

    // Need to use string replace because menu items can be in different places and with different names in languages
    return JSON.parse(
      JSON.stringify(globalNavEsriFooterConfig)
        .replace(/learn\.arcgis\.com\/\$\{lang\}/g, `learn.arcgis.com/en`) // there don't seem to be language-specific learn sites
        .replace(/trust\.arcgis\.com\/\$\{lang\}/g, `trust.arcgis.com/${locale}`)
    );
  }

  /**
   * Converts the relative URLs in the header to absolute URLs, using language-specific versions if possible.
   *
   * @param globalNavEsriFooterConfig Global nav configuration information
   * @param locale The two-letter locale code
   * @return A modified copy of configuration
   */
  private updateHeaderMenuTileUrls(
    globalNavEsriHeaderConfig: any,
    locale: string
  ): any {
    // doc.arcgis.com is only available for some languages
    if ("bs|ca|da|el|hr|hu|id|ko|nb|sk|sl|sr|uk|vi".split("|").includes(locale)) {
      locale = "en";
    }

    // Need to use string replace because menu items can be in different places and with different names in languages
    return JSON.parse(
      JSON.stringify(globalNavEsriHeaderConfig)
        .replace(/\$\{lang\}/g, locale)
        .replace(/"\/en-us\//g, "\"https://www.esri.com/en-us/")
        .replace(/\/content\/dam\/esrisites\/en-us\/common\/icons\//g, "./assets/images/")
        .replace(/\/meridian-\//g, "/global-navigation/")
    );
  }

  // ---------------------------------------------------------------------------------------------------------------- //
  //#region global-nav a11y patches

  /**
   * Performs patching to improve accessibility.
   */
  private performA11yPatching(
  ): void {
    // https://github.com/Esri/global-nav/issues/297
    this.removeElement(document.getElementById("esri-header-inline-title"));
    this.removeAttributes(document.getElementsByClassName("esri-header-account-content-menu"), ["role"]);

    // https://github.com/Esri/global-nav/issues/298
    this.removeAttributes(
      document.getElementById("esri-header-menus-mobile")?.getElementsByClassName("esri-header-menus-list"),
      ["role"]);
    this.removeAttributes(
      document.getElementById("esri-header-menus-desktop")?.getElementsByClassName("esri-header-menus-list"),
      ["aria-labelledby", "role"]);
    this.removeAttributes(
      document.getElementById("esri-header-account-content-menu")?.getElementsByClassName("esri-header-menus-list"),
      ["role"]);

    // https://github.com/Esri/global-nav/issues/300
    this.removeElement(document.getElementsByClassName("esri-header-apps")[0]);
    this.removeElement(document.getElementsByClassName("esri-header-inlineSearch")[0]);
    this.removeElement(document.getElementsByClassName("esri-header-notifications")[0]);
    this.removeElement(document.getElementsByClassName("esri-header-search")[0]);
    this.removeElement(document.getElementsByClassName("esri-header-shopping-cart")[0]);

    // https://github.com/Esri/global-nav/issues/301
    const headerBrand = document.getElementsByClassName("esri-header-brand")[0];
    if (headerBrand) {
      const headerBrandEventListeners = (headerBrand as any).eventListenerList;
      if (headerBrandEventListeners.click) {
        headerBrandEventListeners.click.forEach(
          (handler: EventListenerOrEventListenerObject) => headerBrand.removeEventListener("click", handler)
        );
        delete headerBrandEventListeners.click;
      }
    }

    // https://github.com/Esri/global-nav/issues/302
    this.hideElementFromA11y(document.getElementsByClassName("brand-image")[0]);
    this.hideElementFromA11y(document.getElementsByClassName("esri-header-account-image")[0]);
    this.removeAttribute(document.getElementById("esri-header-brand"), "aria-label");
    this.setInnerHTML(document.getElementsByClassName("esri-header-brand-text")[0], "<h1 class=\"esri-header-brand-text\">ArcGIS Solutions</h1>");

    // https://github.com/Esri/global-nav/issues/303
    this.removeAttributes(document.getElementsByClassName("esri-header-account-signin-menu"), ["role"]);

    // https://github.com/Esri/global-nav/issues/304
    // https://github.com/Esri/global-nav/pull/312
    // https://github.com/ArcGIS/solution-deployment-app/issues/371
    const esriHeaderCanvas = document.getElementsByClassName("esri-header-canvas")[0];
    this.setAttribute(esriHeaderCanvas, "role", "none");
    this.setInnerHTML(esriHeaderCanvas, "scrim");

    // https://github.com/Esri/global-nav/issues/305
    this.removeAttribute(document.getElementById("esri-header-menus-content-desktop"), "aria-expanded");
  }

  /**
   * Marks an element as "aria-hidden".
   *
   * @param element DOM element to hide
   */
  private hideElementFromA11y(
    element: any
  ): void {
    if (element) {
      element.setAttribute("aria-hidden", "true");
    }
  }

  /**
   * Removes attribute from an element.
   *
   * @param element Dom element to modify
   * @param attribute Attribute to remove
   */
  private removeAttribute(
    element: HTMLElement | null,
    attribute: string
  ): void {
    if (element) {
      element.removeAttribute(attribute);
    }
  }

  /**
   * Removes attributes from elements.
   *
   * @param elements Dom elements to modify
   * @param attributes Attributes to remove
   */
  private removeAttributes(
    elements: HTMLCollectionOf<Element> | undefined,
    attributes: string[]
  ): void {
    if (elements) {
      // HTMLCollectionOf<Element> is not an array, so...
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < elements.length; ++i) {
        attributes.forEach(attribute => {
          elements[i].removeAttribute(attribute);
        });
      }
    }
  }

  /**
   * Removes an element's event listeners and the element itself.
   *
   * @param element DOM element to remove
   */
  private removeElement(
    element: any
  ): void {
    if (element) {
      const eventListeners = element.eventListenerList;
      for (const eventName in eventListeners) {
        if (eventListeners.hasOwnProperty(eventName)) {
          eventListeners[eventName].forEach((handler: any) => element.removeEventListener(eventName, handler));
          delete eventListeners[eventName];
        }
      }
      element.remove();
    }
  }

  /**
   * Sets the aria-current attribute on the Home and My Solutions tabs to highlight the active one.
   *
   * @param setHomeAsActive If true, set the Home tab active and the My Solutions tab inactive
   */
  private _setActiveTertiaryMenuTab(
    setHomeAsActive: boolean
  ): void {
    const home = document.getElementById("home-submenu-link");
    if (home) {
      const homeActive = setHomeAsActive ? "true" : "false";
      home.setAttribute("aria-current", homeActive);
      home.parentElement?.setAttribute("aria-current", homeActive);
    }

    const solutions = document.getElementById("solutions-submenu-link");
    if (solutions) {
      const solutionsActive = setHomeAsActive ? "false" : "true";
      solutions.setAttribute("aria-current", solutionsActive);
      solutions.parentElement?.setAttribute("aria-current", solutionsActive);
    }
  }

  /**
   * Adds an attribute to an element.
   *
   * @param element DOM element to modify
   * @param attribute Name of attribute to add
   * @param value Value to give to attribute
   */
  private setAttribute(
    element: any,
    attribute: string,
    value: string
  ): void {
    if (element) {
      element.setAttribute(attribute, value);
    }
  }

  /**
   * Sets the inner HTML of an element.
   *
   * @param element DOM element to modify
   * @param value Value to put into inner HTML
   */
  private setInnerHTML(
    element: any,
    value: string
  ): void {
    if (element) {
      element.innerHTML = value;
    }
  }

  /**
   * Signs out of the app after determining the url to replace the current page.
   */
  private signOut(): void {
    // If we're not authenticated, there's no need to sign out
    /* istanbul ignore else */
    if (this._authentication) {
      if (this._standaloneMode) {
        this._authentication.signOut();
      } else {
        // Sign out and reopen in the gallery mode
        this._authentication.signOutAndRedirect(window.location.href);
      }
    }
  }

  /**
   * Sets the aria-hidden attribute of the second- and third-level menus based on the width of the screen.
   */
  private updateAriaHidden(this: void): void {
    // When the browser is wider than a threshold, the menu is expanded into horizontal tabs and is always visible;
    // when it is narrower, the menu becomes vertical and is hidden. Because the aria-hidden attribute controls
    // whether or not a screen reader will read the menu, we have to be sure to set the attribute based on the
    // visibility of the menu items.

    const secondNavMenu = document.getElementById("second-nav-menu");
    if (secondNavMenu) {
      secondNavMenu.setAttribute("aria-hidden", (window.innerWidth < 976).toString());
    }

    const thirdNavMenu = document.getElementById("third-nav-menu");
    if (thirdNavMenu) {
      thirdNavMenu.setAttribute("aria-hidden", (window.innerWidth < 860).toString());
    }
  }

  //#endregion

}
